import { useEffect } from 'react'
import {
  isActiveElementInput,
  isActiveElementInputWithContent,
} from '@/lib/utils/dom'

interface UseKeyboardNavigationProps {
  isMobile: boolean
  windowCount: number
  activeWindowId: number | null
  onCycleWindows: (direction: 'prev' | 'next') => void
  onScrollWindow: (direction: 'up' | 'down') => void
  onCloseWindow: () => void
  onFocusInput: () => void
  onReorderWindow?: (direction: 'left' | 'right') => void
  onMaximizeWindow?: () => void
  onMinimizeWindow?: () => void
  onOpenChat?: () => void
  onOpenFiles?: () => void
  onOpenSettings?: () => void
  onOpenProfile?: () => void
  onOpenRepresentation?: () => void
}

/**
 * Hook for managing keyboard navigation on the desktop
 */
export function useKeyboardNavigation({
  isMobile,
  windowCount,
  activeWindowId,
  onCycleWindows,
  onScrollWindow,
  onCloseWindow,
  onFocusInput,
  onReorderWindow,
  onMaximizeWindow,
  onMinimizeWindow,
  onOpenChat,
  onOpenFiles,
  onOpenSettings,
  onOpenProfile,
  onOpenRepresentation,
}: UseKeyboardNavigationProps) {
  // Arrow key navigation
  useEffect(() => {
    const handleKeyPress = (event: KeyboardEvent) => {
      if (isMobile) return
      // Allow arrow keys if input is focused but empty, block if it has content
      if (isActiveElementInputWithContent()) return

      if (
        (event.key === 'ArrowLeft' || event.key === 'ArrowRight') &&
        windowCount > 1
      ) {
        // Option/Alt + Arrow keys = reorder windows
        if (event.altKey && onReorderWindow) {
          event.preventDefault()
          onReorderWindow(event.key === 'ArrowLeft' ? 'left' : 'right')
        } else {
          // Regular arrow keys = cycle windows
          event.preventDefault()
          onCycleWindows(event.key === 'ArrowLeft' ? 'prev' : 'next')
        }
      }

      // Up arrow = maximize, Down arrow = minimize
      if (activeWindowId !== null) {
        if (event.key === 'ArrowUp' && onMaximizeWindow) {
          event.preventDefault()
          onMaximizeWindow()
        } else if (event.key === 'ArrowDown' && onMinimizeWindow) {
          event.preventDefault()
          onMinimizeWindow()
        }
      }
    }

    window.addEventListener('keydown', handleKeyPress)
    return () => window.removeEventListener('keydown', handleKeyPress)
  }, [
    isMobile,
    windowCount,
    activeWindowId,
    onCycleWindows,
    onReorderWindow,
    onMaximizeWindow,
    onMinimizeWindow,
  ])

  // Escape key handler
  useEffect(() => {
    const handleEscKey = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        event.preventDefault()

        const activeElement = document.activeElement
        if (activeElement && isActiveElementInput()) {
          ;(activeElement as HTMLElement).blur()
        } else if (activeWindowId !== null) {
          onCloseWindow()
        }
      }
    }

    window.addEventListener('keydown', handleEscKey)
    return () => window.removeEventListener('keydown', handleEscKey)
  }, [activeWindowId, onCloseWindow])

  // Enter key handler
  useEffect(() => {
    const handleEnterKey = (event: KeyboardEvent) => {
      if (event.key === 'Enter' && activeWindowId !== null) {
        if (!isActiveElementInput()) {
          event.preventDefault()
          onFocusInput()
        }
      }
    }

    window.addEventListener('keydown', handleEnterKey)
    return () => window.removeEventListener('keydown', handleEnterKey)
  }, [activeWindowId, onFocusInput])

  // Bracket keys for window reordering
  useEffect(() => {
    const handleReorderKey = (event: KeyboardEvent) => {
      if (isMobile || !onReorderWindow) return
      if (isActiveElementInput()) return
      if (windowCount <= 1) return

      if (event.key === '[' || event.key === ']') {
        event.preventDefault()
        onReorderWindow(event.key === '[' ? 'left' : 'right')
      }
    }

    window.addEventListener('keydown', handleReorderKey)
    return () => window.removeEventListener('keydown', handleReorderKey)
  }, [isMobile, windowCount, onReorderWindow])

  // Window opening shortcuts (c, f, s, p, r)
  useEffect(() => {
    const handleOpenWindowKey = (event: KeyboardEvent) => {
      if (isMobile) return
      if (isActiveElementInput()) return

      // ignore when command key is pressed so as not to conflict with desktop shortcuts
      if (event.metaKey || event.ctrlKey) return

      if (!event.key) return

      switch (event.key.toLowerCase()) {
        case 'c':
          if (onOpenChat) {
            event.preventDefault()
            onOpenChat()
          }
          break
        case 'f':
          if (onOpenFiles) {
            event.preventDefault()
            onOpenFiles()
          }
          break
        case 's':
          if (onOpenSettings) {
            event.preventDefault()
            onOpenSettings()
          }
          break
        case 'p':
          if (onOpenProfile) {
            event.preventDefault()
            onOpenProfile()
          }
          break
        case 'r':
          if (onOpenRepresentation) {
            event.preventDefault()
            onOpenRepresentation()
          }
          break
      }
    }

    window.addEventListener('keydown', handleOpenWindowKey)
    return () => window.removeEventListener('keydown', handleOpenWindowKey)
  }, [isMobile, onOpenChat, onOpenFiles, onOpenSettings, onOpenProfile, onOpenRepresentation])
}
